import { Controller } from "@hotwired/stimulus"
import { formattedUtcTimestamp } from "../../helpers/dateHelpers"

// Manages the terms acceptance UI for one or more PolicyItem components.
//
// Tracks that each policy's download link has been clicked and its checkbox
// checked before enabling the submit button.
//
// Targets (one per PolicyItem rendered inside this controller):
//   downloadLink     — the policy PDF download anchor
//   downloadedMessage — the <p> that shows "Downloaded …" confirmation text
//   checkbox         — the "I agree" checkbox
//   policyItem       — the wrapper div for each policy row
//
// Shared targets (one per controller instance):
//   submitButton     — the Accept & Continue / Confirm button
//   errorMessage     — container for server-side error text
export default class extends Controller {
  static targets = [
    "downloadLink",
    "downloadedMessage",
    "checkbox",
    "policyItem",
    "submitButton",
    "errorMessage",
    "agreedField",
  ]

  connect() {
    if (this.downloadLinkTargets.length === 0) {
      this._renderConfigError(
        "Billing::TermsAcceptance::PolicyItem::STIMULUS_CONTROLLER must be set as a data-controller " +
        "on an element that wraps at least one Billing::TermsAcceptance::PolicyItem."
      )
      return
    }

    if (!this.hasSubmitButtonTarget) {
      this._renderConfigError(
        "No submitButton target found for billing--terms-acceptance. " +
        "Add data-billing--terms-acceptance-target=\"submitButton\" to the form's submit button."
      )
      return
    }

    // Track download state per policy item (index-based, parallel to targets)
    this.downloadedStates = new Array(this.downloadLinkTargets.length).fill(false)

    this.disableSubmit()

    // Store bound handler references so disconnect() can remove the exact same functions
    this._downloadHandlers = this.downloadLinkTargets.map((link, index) => {
      const handler = () => this.handleDownload(index)
      link.addEventListener("click", handler)
      return handler
    })

    // Re-enforce disabled state if another controller (e.g. password validation) enables
    // the button before all TOS conditions are met.
    this._buttonObserver = new MutationObserver(() => {
      if (!this.allPoliciesReady && !this.submitButtonTarget.disabled) {
        this.disableSubmit()
      }
    })
    this._buttonObserver.observe(this.submitButtonTarget, { attributes: true, attributeFilter: ["disabled"] })
  }

  _renderConfigError(message) {
    console.error(`[billing--terms-acceptance] ${message}`)
    const banner = document.createElement("div")
    banner.setAttribute("style", [
      "background: #c0392b",
      "color: #fff",
      "font-weight: bold",
      "padding: 8px 12px",
      "border-radius: 4px",
      "margin: 8px 0",
      "font-family: monospace",
      "font-size: 13px",
    ].join(";"))
    banner.textContent = `⚠ billing--terms-acceptance misconfiguration: ${message}`
    this.element.prepend(banner)
  }

  disconnect() {
    this._buttonObserver?.disconnect()
    if (!this._downloadHandlers) return
    this.downloadLinkTargets.forEach((link, index) => {
      link.removeEventListener("click", this._downloadHandlers[index])
    })
    this._downloadHandlers = []
  }

  handleDownload(index) {
    this.downloadedStates[index] = true
    const message = this.downloadedMessageTargets[index]
    if (message) {
      message.textContent = `Downloaded ${formattedUtcTimestamp()}`
    }
    const checkbox = this.checkboxTargets[index]
    if (checkbox) {
      checkbox.removeAttribute("disabled")
    }
    this.toggleSubmit()
  }

  toggleSubmit() {
    if (this.allPoliciesReady) {
      this.enableSubmit()
    } else {
      this.disableSubmit()
    }
  }

  get allPoliciesReady() {
    const allDownloaded = this.downloadedStates.every(Boolean)
    const allChecked = this.checkboxTargets.every((cb) => cb.checked)
    return allDownloaded && allChecked
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.removeAttribute("disabled")
    }
    if (this.hasAgreedFieldTarget) {
      this.agreedFieldTarget.value = "1"
    }
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.setAttribute("disabled", true)
    }
    if (this.hasAgreedFieldTarget) {
      this.agreedFieldTarget.value = "0"
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

}

